import React, { useCallback, useMemo, useRef } from "react";

import { faSearch } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faCheck } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import { showFeatureModal, UpgradeIcon } from "~/billing";
import { FilterTrigger } from "~/search/filters/Filter";
import { Avatar } from "~/ui";
import { TextField } from "~/ui/legacyFellowIndex";
import Menu from "~/ui/menu";

import type { CommentThreadAccessType } from "~/__generated__/CommentsCreateOrUpdateCommentMutation.graphql";

import styles from "./css/TranscriptCommentsRecipientSelector.module.css";

export type CommentRecipientUserType = { id: string; fullName: string; email: string; isGuest: boolean; image: string };

type UsersRendererProps = {
	query: string;
	selectedUser?: CommentRecipientUserType;
	setSelectedUser: (user: CommentRecipientUserType) => void;
	noteUsers: Array<CommentRecipientUserType>;
};

const MAX_USERS_TO_SHOW = 10;

function UsersRenderer({ query, selectedUser, setSelectedUser, noteUsers }: UsersRendererProps) {
	const usersToRender = useMemo(() => {
		const filteredUsers = query
			? noteUsers.filter(user => user.fullName.toLowerCase().includes(query.toLowerCase()))
			: selectedUser
				? [selectedUser, ...noteUsers.filter(user => user.id !== selectedUser.id)]
				: noteUsers;

		// for notes with too many users, we just show the first MAX_USERS_TO_SHOW to avoid rendering too many users at once
		return filteredUsers.slice(0, MAX_USERS_TO_SHOW);
	}, [noteUsers, query, selectedUser]);

	const onChangeSelectedUser = React.useCallback(
		(user: CommentRecipientUserType) => () => {
			setSelectedUser(user);
		},
		[setSelectedUser],
	);

	if (usersToRender.length === 0) {
		return <span className={styles.emptyState}>No users found</span>;
	}

	return (
		<>
			{usersToRender.map(user => {
				const isSelected = user.id === selectedUser?.id;

				return (
					<Menu.Item key={`user-selection-${user.id}`} onSelect={onChangeSelectedUser(user)}>
						<div className={styles.userRowWrapper}>
							<div className={styles.userRow}>
								<Avatar user={user} size="medium" className={styles.userAvatar} />
								<div>{user.fullName}</div>
							</div>

							{isSelected && (
								<span>
									<FontAwesomeIcon fixedWidth icon={faCheck} className={styles.selectedItemIcon} />
								</span>
							)}
						</div>
					</Menu.Item>
				);
			})}
		</>
	);
}

type TranscriptCommentsRecipientSelectorProps = {
	accessType: CommentThreadAccessType;
	setAccessType: (val: CommentThreadAccessType) => void;
	selectedUser?: CommentRecipientUserType;
	setSelectedUser: (val?: CommentRecipientUserType) => void;
	noteUsers: Array<CommentRecipientUserType>;
	hasAccessToPrivateRecapComments: boolean;
};

export default function TranscriptCommentsRecipientSelector({
	accessType,
	setAccessType,
	selectedUser,
	setSelectedUser,
	noteUsers,
	hasAccessToPrivateRecapComments,
}: TranscriptCommentsRecipientSelectorProps) {
	const [isOpen, setIsOpen] = React.useState<boolean>(false);
	const [search, setSearch] = React.useState<string>("");
	const inputRef = useRef<HTMLInputElement>(null);

	const onInputFocus = useCallback(() => {
		inputRef.current?.focus();
	}, [inputRef]);

	const onChooseUser = React.useCallback(
		(user: CommentRecipientUserType) => {
			setAccessType("INDIVIDUALS");
			setSelectedUser(user);
			setSearch("");
		},
		[setAccessType, setSelectedUser],
	);

	const setRecipientWithClearUser = React.useCallback(
		(value: string) => {
			setAccessType(value as CommentThreadAccessType);
			setSelectedUser(undefined);
			setSearch("");
		},
		[setAccessType, setSelectedUser],
	);

	const showPaywall = React.useCallback(() => {
		showFeatureModal("recap_private_comments");
	}, []);

	const triggerText = accessType === "INDIVIDUALS" && selectedUser ? selectedUser.fullName : "Everyone";

	return (
		<Menu open={isOpen} onOpenChange={setIsOpen}>
			<FilterTrigger triggerContent={triggerText} isOpen={isOpen} />

			<Menu.Content onFocus={onInputFocus}>
				{hasAccessToPrivateRecapComments && (
					<>
						<div className={styles.search}>
							<FontAwesomeIcon icon={faSearch} color="var(--secondaryTextColor)" className="search-icon" />

							<TextField
								autoFocus
								ref={inputRef}
								value={search}
								setValue={setSearch}
								placeholder="Users or email"
								className={styles.searchInput}
							/>
						</div>

						<Menu.Separator />
					</>
				)}

				<Menu.RadioGroup value={accessType} onValueChange={setRecipientWithClearUser}>
					<Menu.RadioItem key="option-everyone" value={"EVERYONE"}>
						Everyone
					</Menu.RadioItem>
				</Menu.RadioGroup>

				<Menu.Separator />

				{hasAccessToPrivateRecapComments ? (
					<UsersRenderer
						query={search}
						selectedUser={selectedUser}
						setSelectedUser={onChooseUser}
						noteUsers={noteUsers}
					/>
				) : (
					<Menu.Item onSelect={showPaywall}>
						<div className={cx(styles.userRowWrapper, styles.wideRow)}>
							<div>Individual user...</div>
							<UpgradeIcon />
						</div>
					</Menu.Item>
				)}
			</Menu.Content>
		</Menu>
	);
}
